import { useConfirm } from 'catalyst-vue'
import type { CollectionSummary } from './api-client'
import { deleteCollectionWithMembers, dissolveCollection } from './collection-service'
import { useCloudLibraryStore } from '../stores/cloud-library'
import { useLibraryStore } from '../stores/library'
import { useLibraryEntriesStore } from '../stores/library-entries'

/**
 * 集合層級的兩個危險動作，書庫頁與集合頁共用同一份確認文字與流程。
 *
 * 解散：集合消失，成員各自回書架，作品與檔案都不動。
 * 刪除連同成員：雲端那一段是一次原子請求（成員套用與批次刪除同一份敘述），之後這台裝置
 * 上持有的成員逐本從書庫移除——硬碟上的檔案一律不動。說明文字逐字對應這個行為。
 */
export function useCollectionActions() {
  const { confirm } = useConfirm()
  const local = useLibraryStore()
  const cloud = useCloudLibraryStore()
  const entries = useLibraryEntriesStore()

  async function dissolve(collection: CollectionSummary): Promise<boolean> {
    let done = false
    await confirm({
      title: `解散「${collection.title}」？`,
      description: `${collection.memberCount} 本成員會各自回到書架上；作品、檔案、閱讀進度與分類都不受影響。`,
      confirmLabel: '解散集合',
      pendingLabel: '正在解散…',
      onConfirm: async () => {
        await dissolveCollection(collection.id)
        await cloud.refresh(true)
        done = true
      },
    })
    return done
  }

  /** 不帶確認的刪除連同成員，供批次刪除使用（確認在呼叫端一次做完）。 */
  async function deleteWithMembersNow(collectionId: string, members: readonly { comicId: string }[] = []): Promise<void> {
    const memberIds = new Set(members.map(member => member.comicId))
    const held = local.comics.filter(comic => comic.cloudComicId && memberIds.has(comic.cloudComicId))
    await deleteCollectionWithMembers(collectionId)
    entries.dropFromSelection([...memberIds])
    for (const comic of held) await local.deleteComic(comic.id)
  }

  async function deleteWithMembers(collection: CollectionSummary): Promise<boolean> {
    const memberIds = new Set(collection.members.map(member => member.comicId))
    const held = local.comics.filter(comic => comic.cloudComicId && memberIds.has(comic.cloudComicId))
    const cloudPart = `${collection.memberCount} 本成員的雲端副本會被刪除，包含圖片、閱讀進度與書籤。`
    const localPart = held.length ? `其中 ${held.length} 本在這台裝置上，會一併從書庫移除；硬碟上的資料夾與壓縮檔不受影響。` : ''
    let done = false
    await confirm({
      title: `刪除「${collection.title}」連同 ${collection.memberCount} 本成員？`,
      description: `${cloudPart}${localPart}這裡沒有復原。`,
      tone: 'danger',
      confirmLabel: `刪除 ${collection.memberCount} 本`,
      pendingLabel: '正在刪除…',
      onConfirm: async () => {
        // 雲端那一段原子完成；本機這一段逐本移除，遇錯即停並讓錯誤浮到確認對話框。
        await deleteWithMembersNow(collection.id, collection.members)
        await cloud.refresh(true)
        done = true
      },
    })
    return done
  }

  return { dissolve, deleteWithMembers, deleteWithMembersNow }
}
